import React, { useRef, useState, useEffect } from 'react'
import styles from './ImportToolPackageModal.module.css'
import { FileJson, Package, RefreshCw, Upload, Library, ClipboardCopy } from 'lucide-react'
import { toolsApi, toolPackagesApi } from '../../lib/apiClient'
import { toast } from '../../lib/toast'
import { useConnectionsStore } from '../../store/connectionsStore'
import ConnectorSelect from '../../ui/ConnectorSelect/ConnectorSelect'
import Button from '../../ui/Button/Button'
import { validatePackageJson } from '../../lib/packageValidation'
import { getTypeMeta } from '../../utils/utils'

interface ImportToolPackageModalProps {
  open: boolean
  onClose: () => void
  onImportComplete: () => void
  initialData?: { package: PackageMeta; tools: any[] }
}

interface PackageMeta {
  name: string
  description?: string
  version: string
  connectionTypeId: number
}

interface BuiltinPackage {
  id: string
  name: string
  description: string
  version: string
  connectionTypeId: number
  toolCount: number
}

interface ParsedPackage {
  package: PackageMeta
  tools: any[]
}

// Max file size: 2 MB
const MAX_FILE_SIZE = 2 * 1024 * 1024

const connectionTypeName = (typeId?: number) =>
  (typeId != null ? getTypeMeta(typeId)?.title : undefined) ?? `type ${typeId ?? 'unknown'}`

const ImportToolPackageModal: React.FC<ImportToolPackageModalProps> = ({ open, onClose, onImportComplete, initialData }) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [parsedData, setParsedData] = useState<ParsedPackage | null>(null)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [connectionId, setConnectionId] = useState('')
  const [mode, setMode] = useState<'merge' | 'replace'>('merge')
  const [importing, setImporting] = useState(false)
  const [builtinPackages, setBuiltinPackages] = useState<BuiltinPackage[]>([])
  const [activeTab, setActiveTab] = useState<'upload' | 'builtin'>('builtin')
  const connectors = useConnectionsStore(state => state.connectors)

  useEffect(() => {
    if (open && initialData) {
      const errors = validatePackageJson(initialData)
      if (errors.length === 0) {
        setParsedData(initialData as ParsedPackage)
        setValidationErrors([])
      } else {
        setValidationErrors(errors)
        setParsedData(null)
      }
    }
  }, [open, initialData])

  useEffect(() => {
    if (open) {
      toolPackagesApi.list(true)
        .then((data: any) => {
          setBuiltinPackages(Array.isArray(data) ? data : [])
        })
        .catch((err: any) => {
          console.error('Failed to load built-in packages:', err)
          setBuiltinPackages([])
        })
    }
  }, [open])

  const reset = () => {
    setParsedData(null)
    setValidationErrors([])
    setConnectionId('')
    setMode('merge')
    setImporting(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Only allow .json files
    if (!file.name.toLowerCase().endsWith('.json')) {
      toast.error('Only .json files are allowed')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    // Size check
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File too large. Maximum size is 2 MB.')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    // MIME type check (browsers may report various types for JSON)
    if (file.type && !['application/json', 'text/json', 'text/plain'].includes(file.type)) {
      toast.error('Invalid file type. Only JSON files are accepted.')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    try {
      const text = await file.text()

      // Reject files with suspicious content before parsing
      if (text.includes('<script') || text.includes('javascript:') || text.includes('data:text/html')) {
        toast.error('File contains potentially malicious content')
        if (fileInputRef.current) fileInputRef.current.value = ''
        return
      }

      let json: any
      try {
        json = JSON.parse(text)
      } catch {
        toast.error('Invalid JSON: failed to parse file')
        if (fileInputRef.current) fileInputRef.current.value = ''
        return
      }

      // Deep validation
      const errors = validatePackageJson(json)
      if (errors.length > 0) {
        setValidationErrors(errors)
        setParsedData(null)
        return
      }

      setValidationErrors([])
      setParsedData(json)
    } catch {
      toast.error('Failed to read file')
    }
  }

  const handleSelectBuiltin = async (pkg: BuiltinPackage) => {
    try {
      const data = await toolPackagesApi.get(pkg.id)
      setValidationErrors([])
      setParsedData(data)
    } catch {
      toast.error('Failed to load package')
    }
  }

  const describeImportError = (message?: string) => {
    const match = message?.match(/Connection type mismatch: package requires type (\d+), but connection is type (\d+)/)
    if (!match) return message || 'Unknown error'
    const connectorName = connectors.find(c => c.id === connectionId)?.connectionName ?? 'the selected connection'
    return `"${connectorName}" is a ${connectionTypeName(Number(match[2]))}, but this package requires a ${connectionTypeName(Number(match[1]))}.`
  }

  const handleImport = async () => {
    if (!parsedData || !connectionId) return
    setImporting(true)
    try {
      const result = await toolsApi.importPackage({
        package: parsedData.package,
        tools: parsedData.tools,
        connectionId,
        mode,
      })
      const parts: string[] = []
      if (result.created > 0) parts.push(`${result.created} created`)
      if (result.updated > 0) parts.push(`${result.updated} updated`)
      if (result.skipped > 0) parts.push(`${result.skipped} skipped`)
      toast.success(`Import complete: ${parts.join(', ')}`)
      onImportComplete()
      handleClose()
    } catch (error: any) {
      toast.error(`Import failed: ${describeImportError(error?.message)}`)
    } finally {
      setImporting(false)
    }
  }

  if (!open) return null

  // // Filter connectors by connectionTypeId
  // const filteredConnectors = parsedData
  //   ? connectors.filter(c => c.connectionTypeId === parsedData.package.connectionTypeId)
  //   : connectors

  const requiredTypeId = parsedData?.package.connectionTypeId
  const selectedConnector = connectors.find(c => c.id === connectionId)
  const typeMismatch = !!selectedConnector && !!requiredTypeId
    && selectedConnector.connectionTypeId !== requiredTypeId

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <h3 className={styles.title}>
          <Package size={20} />
          Import Tool Package
        </h3>

        {!parsedData ? (
          <>
            <div className={styles.tabs}>
              <button
                className={`${styles.tab} ${activeTab === 'builtin' ? styles.tabActive : ''}`}
                onClick={() => { setActiveTab('builtin'); setValidationErrors([]) }}
              >
                <Library size={16} />
                Built-in Packages
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'upload' ? styles.tabActive : ''}`}
                onClick={() => { setActiveTab('upload'); setValidationErrors([]) }}
              >
                <Upload size={16} />
                Upload JSON
              </button>
            </div>

            {validationErrors.length > 0 && (
              <div className={styles.validationErrors}>
                <p>Package validation failed:</p>
                <ul>
                  {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}

            {activeTab === 'upload' ? (
              <div className={styles.uploadArea}>
                <div className={styles.dropZone} onClick={() => fileInputRef.current?.click()}>
                  <FileJson size={32} className={styles.dropIcon} />
                  <p className={styles.dropText}>Click to select a package JSON file</p>
                  <p className={styles.dropHint}>Only .json files up to 2 MB - must contain "package" metadata and "tools" array</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  className={styles.fileInput}
                  onChange={handleFileSelect}
                />
              </div>
            ) : (
              <div className={styles.builtinList}>
                {builtinPackages.length === 0 ? (
                  <p className={styles.emptyText}>No built-in packages available</p>
                ) : (
                  builtinPackages.map(pkg => (
                    <button
                      key={pkg.id}
                      className={styles.builtinItem}
                      onClick={() => handleSelectBuiltin(pkg)}
                    >
                      <div className={styles.builtinInfo}>
                        <span className={styles.builtinName}>{pkg.name}</span>
                        <span className={styles.builtinDesc}>{pkg.description}</span>
                      </div>
                      <span className={styles.builtinMeta}>
                        {pkg.toolCount} tool{pkg.toolCount !== 1 ? 's' : ''} &middot; v{pkg.version}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <div className={styles.fileSummary}>
              <FileJson size={16} />
              <span className={styles.summaryName}>{parsedData.package.name}</span>
              <button className={styles.changeBtn} onClick={reset}>Change</button>
            </div>

            <div className={styles.contentSummary}>
              {parsedData.package.description && (
                <p className={styles.pkgDesc}>{parsedData.package.description}</p>
              )}
              <div className={styles.summaryItem}>
                <span className={styles.summaryCount}>{parsedData.tools.length}</span> tool{parsedData.tools.length !== 1 ? 's' : ''}
                <span className={styles.summaryVersion}>v{parsedData.package.version}</span>
              </div>
            </div>

            <div className={styles.connectionSection}>
              <label className={styles.label}>Select connection for these tools:</label>
              {connectors.length === 0 ? (
                <p className={styles.noConnectors}>No matching connections found for this package type. Create a connection first.</p>
              ) : (
                <ConnectorSelect
                  options={connectors.map(c => ({
                    id: c.id,
                    name: c.connectionName,
                    environment: c.environment,
                  }))}
                  value={connectionId}
                  onChange={setConnectionId}
                  hasError={typeMismatch}
                />
              )}
              {typeMismatch && (
                <p className={styles.typeMismatch}>
                  "{selectedConnector!.connectionName}" is a {connectionTypeName(selectedConnector!.connectionTypeId)},
                  but this package requires a {connectionTypeName(requiredTypeId)}.
                </p>
              )}
            </div>

            <div className={styles.modeSelection}>
              <p className={styles.modeTitle}>Import mode:</p>
              <div className={styles.modeButtons}>
                <button
                  className={`${styles.modeBtn} ${mode === 'merge' ? styles.modeBtnActive : ''}`}
                  onClick={() => setMode('merge')}
                >
                  <ClipboardCopy size={14} /> Skip if exist
                </button>
                <button
                  className={`${styles.modeBtn} ${mode === 'replace' ? styles.modeBtnActive : ''}`}
                  onClick={() => setMode('replace')}
                >
                  <RefreshCw size={14} /> Replace existing
                </button>
              </div>
            </div>

            <div className={styles.actions}>
              <Button variant="cancel" onClick={handleClose}>Cancel</Button>
              <Button
                variant="primary"
                onClick={handleImport}
                disabled={!connectionId || importing}
                loading={importing}
              >
                Import {parsedData.tools.length} Tool{parsedData.tools.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ImportToolPackageModal
